import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RaceColorBar } from "@/components/illustrations/RaceColorBar";
import { registrationEnabled } from "@/config/features";

const KEY_VISUAL_MOBILE = "/bursa-key-visual-mobile-v5.png";
const KEY_VISUAL_DESKTOP = "/key-visual-v2-landscape-2.png";

/**
 * Variant D — Poster-Faithful (key-visual-led).
 *
 * The 2026 landscape key visual (`public/key-visual-v2-landscape-2.png`) fills
 * the desktop hero as a full-width background, starting flush below the sticky
 * header. The slim left column — eyebrow, a one-line intro, the live countdown
 * and the interactive CTAs — sits over a subtle left-edge scrim so the copy
 * stays readable while the artwork shows through on the right. A
 * visually-hidden <h1> keeps a real heading for SEO / assistive tech.
 *
 * The previous hand-built illustration now lives in `HeroRaceScene` (kept for
 * reuse).
 */
export function HeroPosterFaithful() {
  const { t } = useTranslation("home");
  return (
    <section className="relative flex min-h-[177.78vw] flex-col justify-start overflow-hidden bg-background lg:min-h-[56.25vw] lg:justify-center">
      {/* Full-width key-visual background — decorative. */}
      <div aria-hidden className="absolute inset-0 overflow-hidden bg-gradient-to-b from-[#E2EFF8] to-[#D2E5F4]">
        {/* Mobile: purpose-built 9:16 portrait key visual. The section is locked
            to the artwork's 9:16 ratio via min-h-[177.78vw], so object-contain
            renders the WHOLE poster edge-to-edge — top sky through the checkered
            start line, no crop. object-bottom grounds the road above the
            RaceColorBar if the overlaid copy grows the section taller than 9:16,
            where a thin band of the artwork's light-blue sky shows and blends with
            the wrapper gradient. The artwork's light upper area keeps the headline
            readable. */}
        <img
          src={KEY_VISUAL_MOBILE}
          width={1080}
          height={1920}
          loading="eager"
          fetchPriority="high"
          className="pointer-events-none absolute inset-0 h-full w-full object-contain object-bottom lg:hidden"
          alt=""
        />
        {/* Desktop: the section is locked to the artwork's 16:9 ratio via
            lg:min-h-[56.25vw], so object-contain renders the WHOLE landscape key
            visual edge-to-edge — full width, full height, no crop, no side bands.
            object-bottom keeps the road grounded if the section ever grows taller
            than 16:9 (very narrow desktops), where only a thin band of the
            artwork's own light-blue sky shows and blends with the wrapper
            gradient. The wrapper fills the full section, so the artwork is a true
            full-bleed 16:9 with the RaceColorBar trim flush at its bottom. */}
        <img
          src={KEY_VISUAL_DESKTOP}
          width={2560}
          height={1440}
          loading="eager"
          fetchPriority="high"
          className="hidden h-full w-full object-contain object-bottom lg:block"
          alt=""
        />
      </div>

      <div className="container relative w-full pb-16 pt-10 md:py-24 lg:-translate-y-12">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
          {/* ── Slim intro column ──────────────────────────────────────── */}
          <div className="order-2 lg:order-1 lg:max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              <p className="flex w-full items-center gap-3 rounded-full border border-border bg-card px-5 py-1 text-[0.625rem] uppercase tracking-[0.18em] text-muted-foreground shadow-sm lg:inline-flex lg:w-auto lg:gap-2 lg:px-4 lg:py-1.5 lg:text-xs lg:tracking-[0.28em]">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>
                  {t("hero.eyebrowLine1")}<br className="lg:hidden" /> {t("hero.eyebrowLine2")}
                </span>
              </p>
            </motion.div>

            <motion.h1
              className="mt-4 font-display text-4xl leading-none tracking-wide text-balance md:mt-6 md:text-6xl lg:text-7xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
            >
              <span className="block text-primary">{t("hero.headingLine1")}</span>
              <span className="block text-secondary">{t("hero.headingLine2")}</span>
              <span className="sr-only">{t("hero.headingSr")}</span>
            </motion.h1>

            {/* Solid navy accent rule, matching the "RUN FOR ALL." tagline. */}
            <motion.div
              aria-hidden
              className="mt-2 h-1.5 w-32 max-w-full origin-left rounded-full bg-secondary md:mt-5 md:w-96"
              initial={{ opacity: 0, scaleX: 0.6 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
            />

            <motion.p
              className="mt-2 max-w-xl text-sm font-medium text-foreground md:mt-6 md:text-lg lg:max-w-md"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.15 }}
            >
              <span className="lg:hidden">
                {t("hero.introMobileLine1")} <br />{t("hero.introMobileLine2")}
              </span>
              <span className="hidden lg:inline">
                {t("hero.introDesktop")}
              </span>
            </motion.p>

            <motion.div
              className="mt-4 flex w-fit max-w-full flex-col gap-2 sm:mt-9 sm:w-full sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:gap-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
            >
              {registrationEnabled && (
                <Link to="/registration" className="w-full sm:w-auto">
                  <Button size="sm" variant="glow" className="h-10 w-full justify-start px-4 text-sm sm:h-14 sm:w-auto sm:justify-center sm:px-10 sm:text-base">
                    {t("hero.ctaRegister")} <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              )}
              <Link to="/sponsorship" className="w-full sm:w-auto">
                <Button size="sm" variant="outline" className="h-10 w-full justify-start px-4 text-sm sm:h-14 sm:w-auto sm:justify-center sm:px-10 sm:text-base">
                  {t("hero.ctaSponsor")} <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              className="mt-1 md:mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.45 }}
            >
              <span className="text-[0.65rem] font-semibold uppercase tracking-[0.05em] text-foreground/70 sm:text-xs sm:tracking-[0.15em]">
                {t("hero.companiesOnly")}
              </span>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Race-colour trim flush against the bottom of the key-visual */}
      <div className="absolute inset-x-0 bottom-0 z-10">
        <RaceColorBar height={4} className="opacity-90" />
      </div>
    </section>
  );
}
